import { getAddress, type Address } from 'viem'
import type { SwapResponse } from '../api/types'
import { config } from './config'
import { isNative } from './tokens'

/**
 * The check that stands between "the API said so" and "the wallet signed it".
 *
 * `/swap` returns the router it built calldata for and the Permit2 that calldata will pull
 * through. Both are also configured in this app, because the approval flow has to run
 * before any quote exists — and the Permit2 permit is signed over this app's addresses, as
 * its EIP-712 domain and spender. When the two disagree, the approvals and the signature the
 * user granted point at a different contract than the transaction they are about to sign —
 * the swap would revert at best, and at worst the allowance sits with a contract nobody
 * meant to trust.
 *
 * The realistic cause is mundane: this bundle's env drifted from the API's after a redeploy.
 * The failure it prevents is not.
 */
export class AddressMismatchError extends Error {
  constructor(
    readonly field: 'router' | 'permit2',
    readonly expected: Address,
    readonly actual: string
  ) {
    super(
      `the API built calldata for a ${field} this app is not configured for — ` +
        `expected ${expected}, got ${actual}. Check VITE_${field === 'router' ? 'OMEGA_ROUTER' : 'PERMIT2'}_ADDRESS ` +
        `against the API's own environment before swapping.`
    )
    this.name = 'AddressMismatchError'
  }
}

export function assertSwapAddresses(response: SwapResponse): void {
  const to = getAddress(response.to)
  if (to !== config.router) throw new AddressMismatchError('router', config.router, to)
  const permit2 = getAddress(response.permit2)
  if (permit2 !== config.permit2) throw new AddressMismatchError('permit2', config.permit2, permit2)
}

/**
 * The calldata must be for the trade the user is looking at.
 *
 * A route id is opaque, so a mixed-up one cannot be caught by reading it. What can be
 * caught is the plan it expands to disagreeing with the card: different tokens, or an
 * output that is not the one on screen. `/swap` never re-quotes, so a mismatch here means
 * the id did not belong to this card.
 */
export function assertSwapMatchesQuote(
  response: SwapResponse,
  expected: { tokenIn: Address; tokenOut: Address; amountIn: string; amountOut: string }
): void {
  const mismatch =
    getAddress(response.tokenIn.address) !== expected.tokenIn ||
    getAddress(response.tokenOut.address) !== expected.tokenOut ||
    response.amountIn !== expected.amountIn ||
    response.expectedAmountOut !== expected.amountOut

  if (mismatch) {
    throw new Error(
      'the calldata the API returned does not describe the trade on screen. Nothing was signed; re-quote and try again.'
    )
  }

  // The FLR this transaction will carry. Paying in native FLR, it is the amount being swapped
  // and nothing else; paying in an ERC20, it is zero. Anything else is FLR the plan never
  // spends, and whatever the router is left holding, anyone can sweep.
  const expectedValue = isNative(expected.tokenIn) ? BigInt(expected.amountIn) : 0n
  if (BigInt(response.value) !== expectedValue) {
    throw new Error(
      `the API asked to send ${response.value} wei of FLR with this swap, expected ${expectedValue}. Nothing was signed.`
    )
  }
}
